import { useEffect, useState } from 'react';
import { Table, Button, Space, Tag, Modal, Form, Input, Select, message, InputNumber } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { fetchParkings, createParking, updateParking, deleteParking, setCurrentPage, setPageSize } from '../store/slices/parkingsSlice';
// Fonctions utilitaires pour la conversion des montants
const dinarsToMillimes = (dinars: number): number => Math.round(dinars * 1000);
const millimesToDinars = (millimes: number): number => millimes / 1000;

const { Option } = Select;

export function ParkingsPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { parkings, isLoading, total, currentPage, pageSize } = useSelector((state: RootState) => state.parkings);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingParking, setEditingParking] = useState<any>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchParkings({ page: currentPage, pageSize }));
  }, [dispatch, currentPage, pageSize]);

  const handleCreate = () => {
    setEditingParking(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (parking: any) => {
    setEditingParking(parking);
    // Mapper les coordonnées de la base de données vers le format du formulaire
    const formData = {
      ...parking,
      lat: parking.latitude,
      lng: parking.longitude,
      active: parking.isActive,
      type: parking.type || 'PRIVATE', // Valeur par défaut si pas de type
      hourlyRateDinars: parking.hourlyRate ? millimesToDinars(parking.hourlyRate) : undefined,
      dailyRateDinars: parking.dailyRate ? millimesToDinars(parking.dailyRate) : undefined,
    };
    form.setFieldsValue(formData);
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: 'Supprimer le parking',
      content: 'Êtes-vous sûr de vouloir supprimer ce parking ?',
      onOk: () => dispatch(deleteParking(id)),
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingParking) {
        // Mapper les données du formulaire vers le format attendu par l'API
        const updateData = {
          name: values.name,
          address: values.address,
          lat: parseFloat(values.lat),
          lng: parseFloat(values.lng),
          active: values.active,
          type: values.type,
          hourlyRate: values.hourlyRateDinars ? dinarsToMillimes(values.hourlyRateDinars) : undefined,
          dailyRate: values.dailyRateDinars ? dinarsToMillimes(values.dailyRateDinars) : undefined,
        };
        await dispatch(updateParking({ id: editingParking.id, data: updateData })).unwrap();
        message.success('Parking mis à jour');
      } else {
        // Convertir les coordonnées en nombres pour la création aussi
        const createData = {
          name: values.name,
          address: values.address,
          lat: parseFloat(values.lat),
          lng: parseFloat(values.lng),
          type: values.type,
          active: values.active,
          hourlyRate: values.hourlyRateDinars ? dinarsToMillimes(values.hourlyRateDinars) : undefined,
          dailyRate: values.dailyRateDinars ? dinarsToMillimes(values.dailyRateDinars) : undefined,
        };
        await dispatch(createParking(createData)).unwrap();
        message.success('Parking créé');
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Erreur lors de la sauvegarde');
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={type === 'STREET' ? 'blue' : 'green'}>
          {type === 'STREET' ? 'Rue' : 'Privé'}
        </Tag>
      ),
    },
    {
      title: 'Adresse',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'Tarif horaire',
      dataIndex: 'hourlyRate',
      key: 'hourlyRate',
      render: (hourlyRate: number) => 
        hourlyRate ? `${millimesToDinars(hourlyRate).toFixed(3)} TND` : 'Non défini',
    },
    {
      title: 'Tarif journalier',
      dataIndex: 'dailyRate',
      key: 'dailyRate',
      render: (dailyRate: number) => 
        dailyRate ? `${millimesToDinars(dailyRate).toFixed(3)} TND` : 'Non défini',
    },
    {
      title: 'Statut',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Actif' : 'Inactif'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Modifier
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Supprimer
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900">Parkings</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          className="w-full sm:w-auto"
        >
          <span className="hidden sm:inline">Ajouter un parking</span>
          <span className="sm:hidden">Ajouter</span>
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table
          columns={columns}
          dataSource={parkings}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            onChange: (page, size) => {
              dispatch(setCurrentPage(page));
              dispatch(setPageSize(size));
            },
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total}`,
          }}
        />
      </div>

      <Modal
        title={editingParking ? 'Modifier le parking' : 'Nouveau parking'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        width="90%"
        style={{ maxWidth: 600 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Nom"
            rules={[{ required: true, message: 'Veuillez saisir le nom' }]}
          >
            <Input placeholder="Nom du parking" />
          </Form.Item>


          <Form.Item
            name="address"
            label="Adresse"
            rules={[{ required: true, message: 'Veuillez saisir l\'adresse' }]}
          >
            <Input placeholder="Adresse complète" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Veuillez sélectionner le type' }]}
            initialValue="PRIVATE"
          >
            <Select placeholder="Sélectionner le type">
              <Option value="PRIVATE">Privé</Option>
              <Option value="STREET">Public</Option>
            </Select>
          </Form.Item>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Form.Item
              name="lat"
              label="Latitude"
              rules={[{ required: true, message: 'Veuillez saisir la latitude' }]}
            >
              <Input 
                type="text" 
                placeholder="36.8065" 
                onInput={(e) => {
                  // Permettre seulement les nombres et le point décimal
                  e.currentTarget.value = e.currentTarget.value.replace(/[^0-9.-]/g, '');
                }}
              />
            </Form.Item>

            <Form.Item
              name="lng"
              label="Longitude"
              rules={[{ required: true, message: 'Veuillez saisir la longitude' }]}
            >
              <Input 
                type="text" 
                placeholder="10.1815" 
                onInput={(e) => {
                  // Permettre seulement les nombres et le point décimal
                  e.currentTarget.value = e.currentTarget.value.replace(/[^0-9.-]/g, '');
                }}
              />
            </Form.Item>
          </div>


          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Form.Item
              name="hourlyRateDinars"
              label="Tarif horaire (TND)"
              rules={[
                { type: 'number', min: 0, message: 'Le tarif doit être positif' }
              ]}
            >
              <InputNumber
                placeholder="1.500"
                min={0}
                step={0.001}
                precision={3}
                style={{ width: '100%' }}
                addonAfter="TND"
              />
            </Form.Item>

            <Form.Item
              name="dailyRateDinars"
              label="Tarif journalier (TND)"
              rules={[
                { type: 'number', min: 0, message: 'Le tarif doit être positif' }
              ]}
            >
              <InputNumber
                placeholder="15.000"
                min={0}
                step={0.001}
                precision={3}
                style={{ width: '100%' }}
                addonAfter="TND"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="active"
            label="Statut"
            initialValue={true}
          >
            <Select>
              <Option value={true}>Actif</Option>
              <Option value={false}>Inactif</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
